import logoWhiteUrl from "@/assets/svg/logo-white.svg";
import {
  CANVAS_SANS,
  canvasFont as font,
  canvasToPngBlob,
  fitText,
  loadImage,
  roundedRect,
} from "@/shared/utils/canvasDrawing";

export interface PayinQrImageModel {
  qrSrc: string;
  railLabel: string;
  title: string;
  hint: string;
  amountLabel: string;
  amount?: string;
  expiry?: string;
  /** The organization's own Bre-B key, printed only under a QR with no fixed amount. */
  keyLabel?: string;
  keyValue?: string;
}

const WIDTH = 1080;
const PAD = 72;
const TILE = WIDTH - PAD * 2;
const TILE_INSET = 24;
const HEADER_HEIGHT = 64;
const BRAND = "#00735C";
const WHITE = "#FFFFFF";
const WHITE_SOFT = "rgba(255, 255, 255, 0.82)";
const SHADE = "rgba(0, 0, 0, 0.15)";

/** The on-screen placard as a PNG for a chat: full-bleed green so no app paints transparent corners black. */
export async function renderPayinQrImage(model: PayinQrImageModel): Promise<Blob> {
  if (typeof document !== "undefined" && document.fonts) {
    await document.fonts.ready;
  }
  const [logo, qr] = await Promise.all([loadImage(logoWhiteUrl), loadImage(model.qrSrc)]);
  if (!qr) throw new Error("The QR image could not be loaded");

  const amountHeight = model.amount ? 176 : 0;
  const keyHeight = model.keyValue ? 168 : 0;
  const captionHeight = 112 + (model.expiry ? 60 : 0) + keyHeight;
  const height = PAD + HEADER_HEIGHT + 40 + amountHeight + TILE + 48 + captionHeight + PAD;

  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D is not available");

  ctx.fillStyle = BRAND;
  ctx.fillRect(0, 0, WIDTH, height);

  let y = PAD;
  if (logo) {
    const logoHeight = 48;
    const logoWidth = (logo.naturalWidth / logo.naturalHeight) * logoHeight || 280;
    ctx.drawImage(logo, PAD, y + (HEADER_HEIGHT - logoHeight) / 2, logoWidth, logoHeight);
  }
  ctx.font = font(700, 30);
  const chipWidth = ctx.measureText(model.railLabel).width + 56;
  roundedRect(ctx, WIDTH - PAD - chipWidth, y + 6, chipWidth, 52, 26);
  ctx.fillStyle = SHADE;
  ctx.fill();
  ctx.fillStyle = WHITE;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(model.railLabel, WIDTH - PAD - chipWidth / 2, y + 33);

  y += HEADER_HEIGHT + 40;
  if (model.amount) {
    ctx.fillStyle = WHITE_SOFT;
    ctx.font = font(500, 32);
    ctx.fillText(model.amountLabel, WIDTH / 2, y + 20);
    ctx.fillStyle = WHITE;
    ctx.fillText(fitText(ctx, model.amount, TILE, [96, 84, 72], 800, CANVAS_SANS), WIDTH / 2, y + 104);
    y += amountHeight;
  }

  roundedRect(ctx, PAD, y, TILE, TILE, 40);
  ctx.fillStyle = WHITE;
  ctx.fill();
  // Nearest-neighbour keeps every module edge sharp; a smoothed resample greys them for the next camera.
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(qr, PAD + TILE_INSET, y + TILE_INSET, TILE - TILE_INSET * 2, TILE - TILE_INSET * 2);
  ctx.imageSmoothingEnabled = true;
  y += TILE + 48;

  ctx.fillStyle = WHITE;
  ctx.font = font(700, 46);
  ctx.fillText(model.title, WIDTH / 2, y + 24);
  ctx.fillStyle = WHITE_SOFT;
  ctx.font = font(500, 32);
  ctx.fillText(model.hint, WIDTH / 2, y + 84);
  if (model.expiry) {
    ctx.fillStyle = WHITE;
    ctx.font = font(600, 32);
    ctx.fillText(model.expiry, WIDTH / 2, y + 144);
  }
  if (model.keyValue) {
    const keyTop = y + 112 + (model.expiry ? 60 : 0) + 16;
    roundedRect(ctx, PAD, keyTop, TILE, 136, 28);
    ctx.fillStyle = SHADE;
    ctx.fill();
    ctx.fillStyle = WHITE_SOFT;
    ctx.font = font(500, 30);
    ctx.fillText(model.keyLabel ?? "", WIDTH / 2, keyTop + 38);
    ctx.fillStyle = WHITE;
    ctx.fillText(
      fitText(ctx, model.keyValue, TILE - 48, [58, 50, 44], 800, CANVAS_SANS),
      WIDTH / 2,
      keyTop + 96,
    );
  }

  return canvasToPngBlob(canvas);
}
